/**
 *  Class specifies currency format details:
 *      group delimiter format
 *      fraction (decimal) delimiter format
 *      whether to show zero fraction.
 *
 *  @param pattern - string contains explicit information of currency format
 *
 *  Patterns variants:
 *      $##,#    - $10000,12; $10000
 *      ##,#     - 10000,12; 10000
 *      ##,#0    - 10000,12; 10000,00
 *      # #,#   - 10 000,12; 10 000
 *      # #,#0  - 10 000,12; 10 000,00
 *      #,#.#   - 10,000.12; 10,000
 *      #.#,#   - 10.000,12; 10.000
 */
class PatternDetails {
  private groupDelimiter = '';
  private fractionDelimiter = '.';
  private showZeroFraction = false;
  private currencySign = '';

  constructor(pattern: string) {
    this.extractPatternDetails(pattern);
  }

  public getGroupDelimiter(): string {
    return this.groupDelimiter;
  }

  public getFractionDelimiter(): string {
    return this.fractionDelimiter;
  }

  public getShowZeroFraction(): boolean {
    return this.showZeroFraction;
  }

  public getCurrencySign(unitType: string): string {
    switch (unitType) {
      case 'DOLLAR':
        return '$';
      case 'PERCENT':
        return '%';
      case 'NUMBER':
        return '';
      case null :
        return this.currencySign;
    }
  }

  private extractPatternDetails(pattern: string) {
    if (pattern === null) {
      return;
    }
    const delimiters: string[] = pattern.split('#');
    if (delimiters.length < 4) {
      return;
    }

    this.showZeroFraction = delimiters[delimiters.length - 1] === '0';
    this.fractionDelimiter = delimiters[delimiters.length - 2];
    this.groupDelimiter = delimiters[delimiters.length - 3];
    this.currencySign = delimiters[delimiters.length - 4];
  }
}

export class CurrencyFormatter {

  /*  Patterns variants:
  *      $##,#    - $10000,12; $10000
  *      ##,#     - 10000,12; 10000
  *      ##,#0    - 10000,12; 10000,00
  *      # #,#   - 10 000,12; 10 000
  *      # #,#0  - 10 000,12; 10 000,00
  *      #,#.#   - 10,000.12; 10,000
  *      #.#,#   - 10.000,12; 10.000
  */
  public static format(value: any, pattern: string, trimDigits: number = 0,
                       unitType: string, decimalPlaces: number): string {
    if (value === null || value === 0) {
      return value;
    }

    if (isNaN(parseFloat(value))) {
      return value;
    }

    if (typeof value === 'number') {
      value = value.toString();
    }

    const patternDetails = new PatternDetails(pattern);
    return this.formatCurrency(value,
      patternDetails.getGroupDelimiter(),
      patternDetails.getFractionDelimiter(),
      patternDetails.getShowZeroFraction(),
      patternDetails.getCurrencySign(unitType),
      decimalPlaces,
      trimDigits);
  }

  private static formatCurrency(value: string,
                                groupDelimiter: string,
                                fractionDelimiter: string,
                                showZeroFraction: boolean,
                                currencySign: string,
                                decimalPlaces: number,
                                digitsToTrim: number): string {
    // updates value if Trim to thousand selected
    if (digitsToTrim > 0 && currencySign !== '%') {
      value = ((parseFloat(value) / Math.pow(10, digitsToTrim)).toFixed(1)).toString();
    }
    // updates value when currency sign is equal to PERCENT
    if (currencySign === '%') {
      value = (parseFloat(value) * 100).toString();
    }
    const valueParts = value.split('.');
    let wholePart = valueParts[0];
    let decimalPart = valueParts.length > 1 ? valueParts[1] : '';
    wholePart = wholePart.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + groupDelimiter);
    const integerSign = wholePart[0] === '-' ? '-' : '';
    wholePart = wholePart.replace(/-/g, '');

    if (decimalPlaces || showZeroFraction) {
      // append decimal next to specified delimiter if the value contains decimal part
      if (decimalPart.length > 0) {
        decimalPart = this.transformDecimal(decimalPart, decimalPlaces, fractionDelimiter);
      } else {
        if (showZeroFraction && decimalPlaces == null) {
          decimalPart = fractionDelimiter + (digitsToTrim > 0 ? '0' : '00');
        } else {
          decimalPart = decimalPlaces ? fractionDelimiter + '0'.repeat(decimalPlaces) : '';
        }
      }
    }
    // returns result in specific order based on currencySign
    return (currencySign === '%')
      ? integerSign + wholePart + decimalPart + currencySign
      : integerSign + currencySign + wholePart + decimalPart;
  }

  private static transformDecimal(decimalString: string, presicion: number, fractionDelimiter: string): string {
    if (decimalString.length === presicion || presicion === null) {
      return fractionDelimiter + decimalString;
    }
    if (decimalString.length < presicion) {
      return fractionDelimiter + decimalString + '0'.repeat(presicion - decimalString.length);
    }
    if (decimalString.length > presicion) {
      const decimal = parseFloat(decimalString.substr(0, presicion) + '.' + decimalString.substr(presicion + 1));
      return decimal > 0.99 ? fractionDelimiter + Math.floor(decimal).toString() : '';
    }
  }
}

